import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { ColumnType } from "antd/es/table/interface";
import { App, Flex } from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import { DateTime } from "luxon";

import { BaseSurvey } from "@/redux/survey/types";
import QueryTable from "@/components_v2/Tables/QueryTable";
import { ColumnKey } from "@/views/datablocks/types/types";
import {
  SurveyViewFiltersState,
  SurveyViewsTableProps,
} from "@/views/survey-views/types";
import {
  useDeleteSurveyViewMutation,
  useGetCurrentUserQuery, useLazyGetSurveyViewsWithSurveysQuery,
} from "@/redux/service";
import DropdownMenu from "@/components_v2/DropdownMenu/DropdownMenu";
import BaseButton from "@/components_v2/Button/BaseButton";
import { useSurveyViewDownload } from "@/views/survey-views/hooks/useSurveyViewDownload";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import {
  SurveyViewConfig,
  SurveyViewPublicationDestination, SurveyViewWithSurvey,
} from "@/redux/survey-views/types";
import SurveyViewFilters from "@/views/survey-views/components/SurveyViewFilters";
import { OrganizationRoles } from "@/utilities/constants";
import BaseTag from "@/components_v2/Tags/BaseTag";

const PUBLICATION_DESTINATION_TAGS: Record<
  SurveyViewPublicationDestination,
  { className: string; labelKey: string } | undefined
> = {
  [SurveyViewPublicationDestination.WEBSITE]: {
    className: "public",
    labelKey: "surveyViews.labels.website",
  },
  [SurveyViewPublicationDestination.IN_APP]: {
    className: "prme-community",
    labelKey: "surveyViews.labels.in_app",
  },
  [SurveyViewPublicationDestination.EXTERNAL]: undefined,
};

const PublicationDestinationTag = ({
  publicationDestination,
}: {
  publicationDestination: SurveyViewPublicationDestination;
}) => {
  const { t } = useTranslation();

  const tagConfig = PUBLICATION_DESTINATION_TAGS[publicationDestination];
  if (!tagConfig) return null;

  return (
    <BaseTag
      size="small"
      className={tagConfig.className}
      label={t(tagConfig.labelKey)}
    />
  );
};

const SurveyViewsTable = ({
  columnNames,
  filterNames,
  defaultFilters = {},
  additionalColumns,
  ...props
}: SurveyViewsTableProps<SurveyViewWithSurvey>) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notification } = App.useApp();

  const { data: currentUser } = useGetCurrentUserQuery();
  const [filters, setFilters] =
    useState<Partial<SurveyViewFiltersState>>(defaultFilters);
  const [deleteSurveyView] = useDeleteSurveyViewMutation();
  const { downloadSurveyView, isDownloading, downloadingStates } =
    useSurveyViewDownload();

  const handleDeleteSurveyView = async (surveyViewId: string) => {
    deleteSurveyView(surveyViewId)
      .unwrap()
      .then(() => {
        notification.success({
          message: t("surveyViews.labels.surveyViewDeleted"),
        });
      });
  };

  const copyToClipboard = (surveyView: SurveyViewWithSurvey) => {
    let link;
    if (surveyView.version === 1) {
      link = `${window.location.origin}/public/reports/${surveyView.survey}/report?view=${surveyView.id}`;
    } else {
      link = `${window.location.origin}/public/reports/views/${surveyView.id}`;
    }
    navigator.clipboard.writeText(link).then(() => {
      notification.success({ message: t("shareLinkCopied") });
    });
  };

  const canDeleteSurveyView = useCallback((surveyView: SurveyViewWithSurvey) => {
    if (!currentUser) return false;
    return (
      surveyView.config.publicationDestination !==
        SurveyViewPublicationDestination.WEBSITE &&
      (currentUser.id === surveyView.createdBy.id ||
        currentUser.organizationRole === OrganizationRoles.ADMIN.value)
    );
  }, []);

  const canEditSurveyView = useCallback((surveyView: SurveyViewWithSurvey) => {
    if (!currentUser) return false;
    return (
      currentUser.id === surveyView.createdBy.id ||
      currentUser.organizationRole === OrganizationRoles.ADMIN.value
    );
  }, []);

  const columns: Record<string, ColumnType<SurveyViewWithSurvey>> = useMemo(() => {
    const predefinedColumns: Record<string, ColumnType<SurveyViewWithSurvey>> = {
      title: {
        title: t("surveyViews.labels.title"),
        dataIndex: "title",
        ellipsis: true,
        render: (value: string, surveyView: SurveyViewWithSurvey) => {
          return (
            <div className="survey-view-table-title-container">
              {surveyView.version === 1 ? (
                <Link
                  to={`/reports/${surveyView.survey}/report?view=${surveyView.id}`}
                >
                  {value}
                </Link>
              ) : (
                <Link to={`/reports/views/${surveyView.id}`}>{value}</Link>
              )}
              <PublicationDestinationTag
                publicationDestination={
                  surveyView.config.publicationDestination
                }
              />
            </div>
          );
        },
      },
      description: {
        title: t("surveyViews.labels.description"),
        key: "description",
        dataIndex: "description",
        width: 250,
        render: (description: string) => {
          return _.truncate(description, { length: 40 });
        },
      },
      createdAt: {
        title: t("surveyViews.labels.createdAt"),
        key: "createdAt",
        dataIndex: "createdAt",
        width: 250,
        render: (createdAt: string) => {
          return DateTime.fromISO(createdAt).toFormat("dd MMMM yyyy");
        },
      },
      dataFormat: {
        title: t("surveyViews.labels.version"),
        dataIndex: "config",
        render: (config: SurveyViewConfig) =>
          t(`surveyViews.labels.${config.dataFormat.toLowerCase()}`),
      },
      survey: {
        title: t("surveyViews.labels.survey"),
        dataIndex: "survey",
        render: (value: BaseSurvey) => {
          return <Link to={`/reports/${value.id}`}>{value.title}</Link>;
        },
      },
      actions: {
        title: t("surveyViews.labels.actions"),
        key: "actions",
        render: (value: SurveyViewWithSurvey) => {
          const downloading = isDownloading(value.id);
          return (
            <DropdownMenu
              content={
                <div>
                  <BaseButton
                    className="dropdown-menu-button"
                    isFullWidth
                    variant="link"
                    size="small"
                    leadingIcon={<ShareAltOutlined />}
                    onClick={() => copyToClipboard(value)}
                  >
                    {t("surveyViews.labels.copyLink")}
                  </BaseButton>

                  <BaseButton
                    className="dropdown-menu-button"
                    isFullWidth
                    variant="link"
                    size="small"
                    leadingIcon={
                      downloading ? (
                        <BaseSpinner size="small" />
                      ) : (
                        <DownloadOutlined />
                      )
                    }
                    onClick={() =>
                      value.version === 1
                        ? navigate(
                            `/reports/${value.survey}/report?view=${value.id}`,
                          )
                        : downloadSurveyView(value.id)
                    }
                    disabled={downloading}
                  >
                    {downloading
                      ? t("surveyViews.labels.generating")
                      : value.version === 1
                        ? t("buttons.view")
                        : t("surveyViews.labels.download")}
                  </BaseButton>

                  <BaseButton
                    className="dropdown-menu-button"
                    isFullWidth
                    variant="link"
                    size="small"
                    leadingIcon={<ShareAltOutlined />}
                    onClick={() =>
                      navigate("/discussions/form", {
                        state: { surveyView: value },
                      })
                    }
                  >
                    {t("startADiscussion")}
                  </BaseButton>

                  {canEditSurveyView(value) && (
                    <BaseButton
                      className="dropdown-menu-button"
                      isFullWidth
                      variant="link"
                      size="small"
                      leadingIcon={<EditOutlined />}
                      onClick={() =>
                        navigate(
                          value.version === 1
                            ? `/reports/${value.survey}/views/${value.id}/form`
                            : `/reports/views/${value.id}/form`,
                        )
                      }
                    >
                      {t("surveyViews.labels.edit")}
                    </BaseButton>
                  )}
                  {canDeleteSurveyView(value) && (
                    <BaseButton
                      className="dropdown-menu-button"
                      isFullWidth
                      variant="link"
                      size="small"
                      leadingIcon={<DeleteOutlined />}
                      onClick={() => handleDeleteSurveyView(value.id)}
                    >
                      {t("surveyViews.labels.delete")}
                    </BaseButton>
                  )}
                </div>
              }
              placement="bottom"
            >
              <BaseButton variant="link" size="small">
                ...
              </BaseButton>
            </DropdownMenu>
          );
        },
      },
    };
    return { ...predefinedColumns, ...(additionalColumns || {}) };
  }, [downloadingStates]);

  const mappedColumns = useMemo(
    () =>
      columnNames.map(
        (columnKey) =>
          columns[columnKey as ColumnKey] as ColumnType<SurveyViewWithSurvey>,
      ),
    [columns, columnNames],
  );

  return (
    <Flex vertical gap={"small"}>
      <SurveyViewFilters
        filterNames={filterNames}
        setFilters={setFilters}
        defaultFilters={defaultFilters}
      />
      <QueryTable<SurveyViewWithSurvey>
        query={useLazyGetSurveyViewsWithSurveysQuery}
        filters={filters}
        initialSorting={{
          field: "created_at",
          order: "descend",
        }}
        columns={mappedColumns}
        {...props}
      />
    </Flex>
  );
};

export default SurveyViewsTable;
